// Runtime environment. The SPA reads config EXCLUSIVELY from `window.__ENV__`, populated by
// `public/env-config.js.tpl` (web: nginx envsubst at container start) or the wrapper's inject-env-config
// (native: per lane). This is the ONE place hostnames enter the app — ESLint bans http(s):// literals
// everywhere else (the MVP's 377 inline hostnames are why hostname migration is dangerous today).
//
// One-host scheme: each product serves SPA + API + realtime on ONE host by path
// (`/` → SPA, `/api/*` → API, `/ws/*` → realtime), so API_URL/WS_URL resolve to the SAME ORIGIN. The
// OpenAPI paths already carry `/api/v1`, so API_URL is the origin only. The SPA host is swappable later by
// DNS + CORS (keep it a subdomain of the API's registrable domain so the SameSite=Lax reissue cookie stays
// same-site — T-08).

interface CntrlEnv {
  ENV_NAME: string;
  BUILD_NUMBER: string;
  /** API ORIGIN (paths carry /api/v1). "" → same-origin (the one-host default). */
  API_URL: string;
  /** Realtime ORIGIN (path /ws/*). "" → same-origin. */
  WS_URL: string;
  /** The public (Razor) web origin for smart links / EPK, if separated. */
  PUBLIC_WEB_URL: string;
  /** The Antenna fan app apex (antennacntrl.app) — the public club URL base rendered in fan-club settings
   * A CROSS-PRODUCT host, so it is config-injected per lane, never compiled in. "" in dev.*/
  ANTENNA_CLUB_URL: string;
  /** APNs host the native build's device tokens bind to: `'production' | 'sandbox'` (set per lane
   * by inject-env-config). Unset → derived from ENV_NAME (PROD → production, else sandbox), since Xcode-debug
   * and dev/qa builds get sandbox tokens. NEVER hardcoded to production (the named MVP defect).*/
  APP_ENV?: string;
  /** "1" hides dev-only chrome (the "View as" switcher) so App Store screenshots show the app as a real
   * user sees it. Set by the native wrapper's inject-env-config (`SCREENSHOT_MODE=1 pnpm build:local`).*/
  SCREENSHOT_MODE?: string;
  /** The app's App Store page, opened from the "Update required" screen when the API refuses this build
   * (`clientVersion.ts`). Set per lane by the native wrapper's inject-env-config; unset ⇒ the screen shows no button.*/
  APP_STORE_URL?: string;
}

/** Compile-time default used under `vite dev`, where env-config.js is never rendered (same-origin dev). */
const DEV_ENV: CntrlEnv = {
  ENV_NAME: "LOCAL",
  BUILD_NUMBER: "0",
  API_URL: "", // same-origin — Vite proxies /api and /ws to the local core-api (see vite.config.ts)
  WS_URL: "",
  PUBLIC_WEB_URL: "",
  ANTENNA_CLUB_URL: "", // injected per lane (antennacntrl.app); "" in dev renders a relative /{handle}
};

const winEnv = (globalThis as { __ENV__?: Partial<CntrlEnv> }).__ENV__;

const resolved: CntrlEnv = {
  ...DEV_ENV,
  ...(winEnv ?? {}),
};

// Strip a trailing slash so `${API_URL}/api/v1/...` never doubles up.
const stripTrailingSlash = (s: string) => s.replace(/\/+$/, "");

export const ENV_NAME = resolved.ENV_NAME;

/**
 * The lanes where DEV-ONLY surfaces may exist. Mirrors `EnvironmentNames.cs` on the backend — the same
 * allowlist, the same fail-closed default — so the two halves of a dev-only feature can never disagree about
 * which environment they are in.
 *
 * ALLOWLIST, not an "is it PROD?" test. Naming the dangerous lane treats every unanticipated value as safe:
 * `PRODUCTION` (a spelling this repo already uses), `PRD`, a future `prod-eu`, or an UNSET `ENV_NAME` all
 * pass a `!== "PROD"` check. Listing the lanes we know are safe means a novel name hides the surface instead
 * of exposing it.
 *
 * NOTE this is a RUNTIME check, and it has to be: the SPA is built ONCE and configured per lane from
 * `window.__ENV__`, so one artifact serves every environment and there is no build-time constant to branch
 * on. A route guarded by this does not RESOLVE in production, but its component bytes are still in the
 * bundle — the gate is reachability, not absence.
 */
const NON_PRODUCTION_LANES = ["LOCAL", "DEV", "QA"];

/** True only in an explicitly-recognised non-production lane. Unset ⇒ false (fail closed). */
export const IS_NON_PRODUCTION = NON_PRODUCTION_LANES.includes(ENV_NAME.trim().toUpperCase());
/** Screenshot builds hide dev-only chrome. Only ever HIDES things, so it needs no lane gate. */
export const IS_SCREENSHOT_MODE = resolved.SCREENSHOT_MODE === "1";
export const BUILD_NUMBER = resolved.BUILD_NUMBER;
export const API_URL = stripTrailingSlash(resolved.API_URL);
export const WS_URL = stripTrailingSlash(resolved.WS_URL);
export const PUBLIC_WEB_URL = stripTrailingSlash(resolved.PUBLIC_WEB_URL);
export const ANTENNA_CLUB_URL = stripTrailingSlash(resolved.ANTENNA_CLUB_URL);
export const APP_STORE_URL = resolved.APP_STORE_URL ?? "";

/** The APNs environment for native push registration — explicit `__ENV__.APP_ENV`, else derived from the lane
 * (PROD → production; LOCAL/DEV/QA → sandbox). The sender's BadDeviceToken→sandbox retry is belt-and-suspenders,
 * not a substitute for setting this per lane.*/
export const APP_ENV: "production" | "sandbox" =
  resolved.APP_ENV === "production" || resolved.APP_ENV === "sandbox"
    ? resolved.APP_ENV
    : ENV_NAME === "PROD"
      ? "production"
      : "sandbox";

/** The realtime hub URL — same-origin `/ws/live` by default.*/
export const HUB_URL = `${WS_URL}/ws/live`;
